import { useEffect, useRef, useState } from 'react';
import { useSubmit, useActionData, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Constants
import { formActionValidationNaming } from '../../../../components/forms/ActionForms/constants';

// Utils
import { createFormDataFromValues } from '../../../../utils/forms';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';
import { difference } from '../../../../utils/difference';

// Types
import { TabValidationStatus } from '../../../../utils/formValidation';
import { Role } from '../../../../types/auth';

// Redux
import { openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../../components/modals/SuperModal/types';

type ActionValidationResult = {
  validationFailed?: boolean;
  validationErrors?: Array<{
    tab: string;
    tabKey: string;
    field: string;
    message: string;
  }>;
  tabsWithErrors?: TabValidationStatus[];
  validationSummary?: string;
  error?: boolean;
  errorMessage?: string;
};

export type SubmitIntent = 'save' | 'validate' | 'pending-certification';

/**
 * Fechas opcionales borrables con la papelera del form de Operarios y
 * Vehículos. Para ellas un diff con valor '' significa "el usuario la borró"
 * y debe viajar al save (que lo traduce a null = borrar en BD), no filtrarse.
 */
const CLEARABLE_DATE_KEYS = ['registerEndDate', 'registerArrivalDate'];

/** Rutas de cada pestaña relativas a la pestaña ACTUAL (la terminal). */
const TAB_PATHS = [
  'operators-and-vehicles',
  'location',
  'edition',
  'budget-items',
  'materials',
  'machinery',
  'animals',
] as const;

export type TerminalTab = 'animals' | 'materials';

/**
 * Envío del PASO TERMINAL de la validación de una actuación.
 *
 * Vivía dentro de `ActionsValidation/Animals/index.tsx` porque Animals era el
 * último paso de todos los asistentes. Desde 2026-08-11 la actuación BÁSICA
 * no tiene esa pestaña y termina en Materials, así que la orquestación —el
 * diff de TODOS los formularios, los tres intents y el modal de errores— pasa
 * a ser compartida en vez de duplicarse.
 *
 * `terminalTab` NO es decorativo: el modal de errores navega a la pestaña que
 * falló, y la entrada que apunta a `'.'` es la del paso terminal. Con el mapa
 * fijo a `animals`, un error de la básica mandaba al usuario a una pestaña que
 * ya no existe.
 *
 * Spec: docs/superpowers/specs/2026-08-11-basica-sin-fauna-design.md
 */
export const useActionValidationSubmit = ({
  isBasicAction,
  isReadOnly,
  userRole,
  terminalTab,
}: {
  isBasicAction: boolean;
  isReadOnly: boolean;
  userRole: Role | null;
  terminalTab: TerminalTab;
}) => {
  const submit = useSubmit();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const actionData = useActionData() as ActionValidationResult | undefined;
  const [error, setError] = useState<string | null>(null);
  const submitIntentRef = useRef<SubmitIntent>('validate');

  const setIntent = (intent: SubmitIntent) => {
    submitIntentRef.current = intent;
  };

  // Modal de errores de validación: navega a la pestaña que falló.
  useEffect(() => {
    if (!actionData?.validationFailed || !actionData?.tabsWithErrors) return;

    const tabRoutes: Record<string, string> = Object.fromEntries(
      TAB_PATHS.map((tab) => [tab, tab === terminalTab ? '.' : `../${tab}`])
    );

    dispatch(
      openModal(modalIds.VALIDATION_ERRORS_MODAL, {
        tabsWithErrors: actionData.tabsWithErrors,
        validationSummary: actionData.validationSummary,
        onNavigateToTab: (tabKey: string) => {
          const route = tabRoutes[tabKey] || `../${tabKey}`;
          navigate(route, { state: { validateOnMount: true } });
        },
      })
    );
  }, [actionData, dispatch, navigate, terminalTab]);

  const onFormSubmit = () => {
    // RoomTeam cannot validate
    if (isReadOnly) return;

    // El formulario de operarios lo precarga la página del servidor (S2,
    // 2026-10-07); antes solo existía si su pestaña se llegaba a montar y
    // cambiaba algo. Aun así puede faltar —la precarga falló, sin red— y
    // `JSON.parse(null).operatorsTable` reventaba con un TypeError: el botón
    // dejaba de funcionar EN SILENCIO. Sin formulario = la pestaña no se tocó:
    // ni se comprueba ni se manda nada de la tripulación, y `saveData` la deja
    // quieta. La básica no lo lee nunca: su pestaña está oculta.
    const operatorsFormRaw = isBasicAction
      ? null
      : sessionStorage.getItem(
          formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM
        );
    const operatorsAndVehiclesData = operatorsFormRaw
      ? JSON.parse(decompressSessionStorage(operatorsFormRaw))
      : null;

    const operatorsLength = operatorsAndVehiclesData
      ? (operatorsAndVehiclesData.operatorsTable ?? []).filter(
          (item: null | string) => item
        ).length
      : 0;
    const missingOperators =
      !!operatorsAndVehiclesData &&
      operatorsLength === 0 &&
      !operatorsAndVehiclesData.isExternal;

    if (missingOperators) {
      setError('Heu de seleccionar almenys un operador');
    }

    if (!missingOperators) {
      const dataChanged: any = {};

      // En la básica no se lee la sesión de Operarios: su pestaña está oculta,
      // así que lo que haya ahí es de OTRA actuación y colaría fechas ajenas
      // (que el back marcaría como «puestas a mano»). Hallazgo A7, 12-oct.
      const diffKeys = Object.values(formActionValidationNaming).filter(
        (key) =>
          !isBasicAction ||
          key !==
            formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM
      );

      diffKeys.forEach((key) => {
        const dataFromCacheRaw = sessionStorage.getItem(key);
        if (dataFromCacheRaw) {
          const dataFromCache = JSON.parse(
            decompressSessionStorage(dataFromCacheRaw as string)
          );

          if (dataFromCache && dataFromCache.dataFromServer) {
            const { dataFromServer } = dataFromCache;
            delete dataFromCache.dataFromServer;

            const dataDifference: any = difference(
              dataFromCache,
              dataFromServer
            );

            Object.keys(dataDifference).forEach((diffKey) => {
              if (dataDifference[diffKey]) {
                dataChanged[diffKey] = dataDifference[diffKey];
              } else if (
                CLEARABLE_DATE_KEYS.includes(diffKey) &&
                dataDifference[diffKey] === ''
              ) {
                // Borrado con la papelera: el diff da '' (cache vacía vs fecha
                // en server). El filtro truthy de arriba lo descartaba y el
                // borrado nunca viajaba al save — la fecha "no se borraba".
                // '' viaja en el formData y saveData lo traduce a null.
                dataChanged[diffKey] = '';
              }
            });
          }
        }
      });

      const formData = createFormDataFromValues({
        ...dataChanged,
        // La básica no manda nada del formulario de operarios: ni `isExternal`
        // ni `isSimultaneous`. Este último era la vía por la que se podía
        // desmarcar «Simultánea» desde la validación y hacer que la actuación
        // sumara horas ya contadas en el turno del recorrido (doble conteo).
        // El backend además lo fuerza —cinturón y tirantes—, pero el front no
        // debe ni intentarlo. Sin formulario de operarios tampoco: ausente =
        // «no tocar»; un valor inventado pisaría el del servidor.
        ...(operatorsAndVehiclesData
          ? {
              isExternal: operatorsAndVehiclesData.isExternal,
              isSimultaneous: operatorsAndVehiclesData.isSimultaneous ?? false,
            }
          : {}),
        userRole: userRole,
        intent: submitIntentRef.current,
        // Viaja al route action y al saveData: los dos necesitan saber que es
        // una básica para saltarse las pestañas que no existe —operarios y,
        // desde 2026-08-11, fauna— sin volver a consultar nada. Por DATO: el
        // gating por `pathname` del wizard de creación no se cumple aquí
        // (esto vive en /work-orders/).
        isBasicAction: isBasicAction ? 'true' : 'false',
      });
      submit(formData, {
        method: 'POST',
        action: '.',
      });
    }
  };

  return { onFormSubmit, error, setIntent };
};
